import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const SubscriptionAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30days');

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: '1year', label: 'Last Year' }
  ];

  // ── Plan Distribution (จาก user_profiles.subscription_tier) ─────────────────
  const [planDistributionData, setPlanDistributionData] = useState([]);

  const TIER_CONFIG = {
    free:  { label: 'Free',  color: '#94A3B8' },  // text-muted-foreground
    solo:  { label: 'Solo',  color: '#fdba74' },  // text-orange-300
    pro:   { label: 'Pro',   color: '#F77F00' },  // text-accent
    team:  { label: 'Team',  color: '#E85D04' },  // text-primary
  };

  useEffect(() => {
    const fetchPlanDistribution = async () => {
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('subscription_tier');

        if (error) throw error;

        // นับจำนวน user แต่ละ tier
        const counts = {};
        (data || []).forEach((u) => {
          const tier = (u.subscription_tier || 'free').toLowerCase();
          counts[tier] = (counts[tier] || 0) + 1;
        });

        // แปลงเป็น chart data (เฉพาะ tier ที่มี user)
        const result = Object.entries(counts)
          .map(([tier, count]) => ({
            name: TIER_CONFIG[tier]?.label || tier,
            value: count,
            color: TIER_CONFIG[tier]?.color || '#94A3B8',
          }))
          .sort((a, b) => b.value - a.value);

        setPlanDistributionData(result);
      } catch (err) {
        console.error('Error fetching plan distribution:', err);
      }
    };

    fetchPlanDistribution();
  }, []);

  // ── Revenue Trending (6 เดือนย้อนหลัง จาก Supabase) ────────────────────────
  const [revenueData, setRevenueData] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    const fetchRevenueTrending = async () => {
      try {
        const now = new Date();
        // เริ่มจาก 6 เดือนก่อน (ต้นเดือน)
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);

        const { data, error } = await supabase
          .from('revenue_records')
          .select('amount, paid_at')
          .eq('status', 'success')
          .gte('paid_at', sixMonthsAgo.toISOString())
          .lt('paid_at', nextMonthStart.toISOString());

        if (error) throw error;

        // จัดกลุ่มตามเดือน
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthlyMap = {};

        // สร้าง 6 เดือน (รวมเดือนปัจจุบัน)
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${d.getMonth()}`;
          monthlyMap[key] = { month: monthNames[d.getMonth()], revenue: 0 };
        }

        // รวมยอดตาม records
        (data || []).forEach((r) => {
          const paidDate = new Date(r.paid_at);
          const key = `${paidDate.getFullYear()}-${paidDate.getMonth()}`;
          if (monthlyMap[key]) {
            monthlyMap[key].revenue += Number(r.amount);
          }
        });

        const result = Object.values(monthlyMap);
        const total = result.reduce((sum, m) => sum + m.revenue, 0);

        setRevenueData(result);
        setTotalRevenue(total);
      } catch (err) {
        console.error('Error fetching revenue trending:', err);
      }
    };

    fetchRevenueTrending();
  }, []);

  // ── Churn Rate (6 เดือนย้อนหลัง จาก user_subscriptions) ─────────────────────
  const [churnData, setChurnData] = useState([]);
  const [avgChurn, setAvgChurn] = useState(0);

  useEffect(() => {
    const fetchChurnRate = async () => {
      try {
        const now = new Date();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const results = [];

        for (let i = 5; i >= 0; i--) {
          const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);

          // Active subscriptions ต้นเดือน
          // = subscription ที่ start_date < ต้นเดือน AND (end_date >= ต้นเดือน OR end_date = null)
          const { count: activeCount } = await supabase
            .from('user_subscriptions')
            .select('id', { count: 'exact', head: true })
            .lt('start_date', monthStart.toISOString())
            .or(`end_date.gte.${monthStart.toISOString()},end_date.is.null`);

          // Churned ในเดือนนั้น:
          // 1) cancelled_at อยู่ในช่วงเดือนนั้น
          const { count: cancelledCount } = await supabase
            .from('user_subscriptions')
            .select('id', { count: 'exact', head: true })
            .gte('cancelled_at', monthStart.toISOString())
            .lt('cancelled_at', monthEnd.toISOString());

          // 2) end_date อยู่ในช่วงเดือนนั้น + status ไม่ใช่ active (หมดอายุไม่ต่อ)
          const { count: expiredCount } = await supabase
            .from('user_subscriptions')
            .select('id', { count: 'exact', head: true })
            .gte('end_date', monthStart.toISOString())
            .lt('end_date', monthEnd.toISOString())
            .neq('status', 'active')
            .is('cancelled_at', null); // ไม่นับซ้ำกับ cancelled

          const churned = (cancelledCount ?? 0) + (expiredCount ?? 0);
          const active = activeCount ?? 0;
          const churnRate = active > 0 ? ((churned / active) * 100) : 0;

          results.push({
            month: monthNames[monthStart.getMonth()],
            churn: parseFloat(churnRate.toFixed(1)),
          });
        }

        setChurnData(results);

        // Average Churn Rate
        const total = results.reduce((sum, m) => sum + m.churn, 0);
        setAvgChurn(results.length > 0 ? parseFloat((total / results.length).toFixed(1)) : 0);
      } catch (err) {
        console.error('Error fetching churn rate:', err);
      }
    };

    fetchChurnRate();
  }, []);

  // ── Tool Usage Distribution (จาก projects.tool_type ใน Supabase) ──────
  const TOOL_COLORS = [
    '#E85D04', '#F77F00', '#F59E0B', '#fdba74', '#FB923C',
    '#2563EB', '#3B82F6', '#60A5FA', '#8B5CF6', '#A78BFA',
  ];

  const TOOL_TYPE_LABELS = {
    'moodboard-to-3d': 'Moodboard to 3D Room',
    'sketch-to-render': 'Sketch to Interior Render',
    'cad-mood-room': 'CAD-Based Mood Room',
    'room-variation': 'Room Variation Generator',
    'combine-styles': 'Combine Interior Styles',
    'freestyle': 'Freestyle Design',
    'site-concept': 'Site Concept',
    'complete-room-design': 'Complete Room Design',
    'furniture-editing': 'Furniture Editing',
    'cad-space-planner': 'CAD Space Planner',
  };

  const [toolUsageData, setToolUsageData] = useState([]);

  useEffect(() => {
    const fetchToolUsage = async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('tool_type');

        if (error) throw error;

        const counts = {};
        (data || []).forEach((p) => {
          const tt = p.tool_type;
          if (tt) counts[tt] = (counts[tt] || 0) + 1;
        });

        const result = Object.keys(TOOL_TYPE_LABELS)
          .map((key) => ({
            tool: TOOL_TYPE_LABELS[key],
            projects: counts[key] || 0,
          }))
          .sort((a, b) => b.projects - a.projects);

        setToolUsageData(result);
      } catch (err) {
        console.error('Error fetching tool usage:', err);
      }
    };

    fetchToolUsage();
  }, []);

  const totalProjects = toolUsageData.reduce((sum, t) => sum + t.projects, 0);
  const maxProjects = Math.max(...toolUsageData.map(t => t.projects), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Subscription Analytics</h2>
        <Select
          options={timeRangeOptions}
          value={timeRange}
          onChange={setTimeRange}
          className="w-40"
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border shadow-soft p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="PieChart" size={20} className="text-primary" />
            Plan Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={planDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {planDistributionData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {planDistributionData?.map((item, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }}></div>
                  <span className="text-foreground">{item?.name}</span>
                </div>
                <span className="font-semibold text-foreground">{item?.value} users</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-soft p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={20} className="text-success" />
            Monthly Revenue
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-popover)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="revenue" fill="#2563EB" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Revenue (6 months)</span>
            <span className="text-xl font-semibold text-foreground">${totalRevenue.toLocaleString()}</span>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-soft p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="TrendingDown" size={20} className="text-error" />
            Churn Rate
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={churnData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-popover)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="churn" stroke="#DC2626" strokeWidth={2} dot={{ fill: '#DC2626', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Average Churn Rate</span>
            <span className="text-xl font-semibold text-foreground">{avgChurn}%</span>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-soft p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Wrench" size={20} className="text-accent" />
            Tool Usage Distribution
          </h3>
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-2 orange-scrollbar">
            {toolUsageData.map((item, index) => {
              const percentage = maxProjects > 0 ? (item.projects / maxProjects) * 100 : 0;
              const color = TOOL_COLORS[index % TOOL_COLORS.length];
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-foreground truncate mr-3" title={item.tool}>{item.tool}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm font-semibold text-foreground">{item.projects}</span>
                      <span className="text-xs text-muted-foreground w-10 text-right">
                        {totalProjects > 0 ? ((item.projects / totalProjects) * 100).toFixed(0) : 0}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-2.5 rounded-full bg-border/50 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out"
                      style={{
                        width: `${percentage}%`,
                        background: `linear-gradient(90deg, ${color}, ${color}CC)`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Projects</span>
            <span className="text-xl font-semibold text-foreground">{totalProjects.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionAnalytics;